import { Trans } from "@lingui/macro";
import cx from "classnames";
import { useState } from "react";

import type { Address } from "viem";

import { selectChartToken } from "context/SyntheticsStateContext/selectors/chartSelectors";
import { selectChainId } from "context/SyntheticsStateContext/selectors/globalSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { use24hPriceDeltaMap } from "domain/synthetics/tokens/use24hPriceDeltaMap";

import { useHyperliquidBook, type BookRow } from "./useHyperliquidBook";

const BOOK_ROWS = 11;

const HEAD = "grid grid-cols-3 px-14 py-9 text-11 tracking-[0.4px] text-typography-secondary bg-slate-800";
const ROW = "relative grid cursor-default grid-cols-3 px-14 py-[4.5px] text-[12.5px] numbers";

type Tab = "book" | "trades";

function num(value: number, digits: number) {
  return value.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Sizes span orders of magnitude across coins, so scale the precision. */
function sizeDigits(value: number) {
  if (value >= 1000) return 1;
  if (value >= 1) return 3;
  return 5;
}

function utcTime(ms: number) {
  return new Date(ms).toISOString().slice(11, 19);
}

function DepthRows({ rows, side }: { rows: BookRow[]; side: "ask" | "bid" }) {
  const isAsk = side === "ask";

  return (
    <>
      {rows.map((row, index) => (
        <div key={`${side}-${row.price}-${index}`} className={cx(ROW, isAsk ? "usdx-ask-row" : "usdx-bid-row")}>
          <span
            className="absolute inset-y-0 right-0 transition-[width] duration-500 ease-out"
            style={{
              width: `${(row.depth * 100).toFixed(1)}%`,
              background: isAsk ? "var(--usdx-depth-ask)" : "var(--usdx-depth-bid)",
            }}
            aria-hidden="true"
          />
          <span className={cx("relative font-semibold", isAsk ? "text-red-500" : "text-green-500")}>
            {num(row.price, row.price >= 1000 ? 1 : 4)}
          </span>
          <span className="relative text-right text-typography-primary">{num(row.size, sizeDigits(row.size))}</span>
          <span className="relative text-right text-typography-secondary">
            {num(row.total, sizeDigits(row.total))}
          </span>
        </div>
      ))}
    </>
  );
}

type Props = {
  className?: string;
};

/**
 * Order book / recent trades, in the design's layout.
 *
 * Levels come from Hyperliquid's public L2 feed — see `useHyperliquidBook` for
 * why that venue and not the GMX pools.
 */
export function UsdxOrderBook({ className }: Props) {
  const [tab, setTab] = useState<Tab>("book");
  const chainId = useSelector(selectChainId);
  const { chartToken } = useSelector(selectChartToken);
  const coin = chartToken?.symbol;

  const { asks, bids, trades, mid, isLoading, error } = useHyperliquidBook(coin, BOOK_ROWS);

  /* Same 24h source the chart header uses, so the two never disagree. */
  const deltaMap = use24hPriceDeltaMap(chainId, [chartToken?.address as Address | undefined]);
  const changePercent = chartToken?.address ? deltaMap?.[chartToken.address]?.deltaPercentage : undefined;

  const isUp = (changePercent ?? 0) >= 0;

  const tabs: { key: Tab; label: React.ReactNode }[] = [
    { key: "book", label: <Trans>Order Book</Trans> },
    { key: "trades", label: <Trans>Trades</Trans> },
  ];

  const showEmpty = !isLoading && (error || (tab === "book" ? !asks.length && !bids.length : !trades.length));

  return (
    <section className={cx("usdx-card flex flex-col overflow-hidden", className)}>
      <div className="grid shrink-0 grid-cols-2">
        {tabs.map((item) => {
          const isActive = tab === item.key;

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={cx(
                "border-b-2 py-13 text-[13.5px] font-semibold transition-all",
                isActive
                  ? "border-b-blue-500 bg-slate-800 text-blue-500"
                  : "border-b-slate-600 text-typography-secondary"
              )}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 grow overflow-y-auto scrollbar-hide">
        {showEmpty ? (
          <div className="px-14 py-24 text-center text-12 text-typography-secondary">
            {error ?? <Trans>No data for this market</Trans>}
          </div>
        ) : tab === "book" ? (
          <>
            <div className={HEAD}>
              <span>
                <Trans>Price</Trans>
              </span>
              <span className="text-right">
                <Trans>Size</Trans> {coin ? `(${coin})` : null}
              </span>
              <span className="text-right">
                <Trans>Total</Trans> {coin ? `(${coin})` : null}
              </span>
            </div>

            <DepthRows rows={asks} side="ask" />

            {/* Mid band — the design's hero price row between the two sides. */}
            <div className="flex items-center justify-between border-y border-slate-600 bg-slate-800 px-14 py-10">
              <span className="usdx-display text-24 text-blue-300 numbers">
                {mid === undefined ? "—" : `$${num(mid, mid >= 1000 ? 2 : 4)}`}
              </span>
              {changePercent !== undefined && (
                <span className={cx("text-13 font-bold numbers", isUp ? "text-green-500" : "text-red-500")}>
                  {isUp ? "+" : ""}
                  {changePercent.toFixed(2)}%
                </span>
              )}
            </div>

            <DepthRows rows={bids} side="bid" />
          </>
        ) : (
          <>
            <div className={HEAD}>
              <span>
                <Trans>Price</Trans>
              </span>
              <span className="text-right">
                <Trans>Size</Trans> {coin ? `(${coin})` : null}
              </span>
              <span className="text-right">
                <Trans>Time</Trans>
              </span>
            </div>

            {trades.map((trade, index) => (
              <div key={`${trade.time}-${index}`} className={cx(ROW, "usdx-row-hover")}>
                <span className={cx("font-semibold", trade.isBuy ? "text-green-500" : "text-red-500")}>
                  {num(trade.price, trade.price >= 1000 ? 1 : 4)}
                </span>
                <span className="text-right text-typography-primary">{num(trade.size, sizeDigits(trade.size))}</span>
                <span className="text-right text-typography-secondary">{utcTime(trade.time)}</span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* The book is venue-specific; say so rather than implying it is GMX depth. */}
      <div className="shrink-0 border-t border-slate-600 px-14 py-8 text-[10.5px] text-typography-secondary">
        <Trans>Depth via Hyperliquid</Trans>
      </div>
    </section>
  );
}
